import { foundation } from '../../../ui';
import * as p from '../../../ui/primitives';
import {
  AUTO_HEIGHT_WEB_MS, AUTO_HEIGHT_WEB_EASE, AUTO_HEIGHT_MOBILE_MS, AUTO_HEIGHT_MOBILE_EASE,
} from '../../common/AutoHeight';
import { modalFlexColumnShell, modalPinnedFooterRow } from '../../../ui/overlayStyles';

const f = foundation;
const fc = f.color;

/**
 * RD-SEC-015 visual polish (2026-09-25) — the ONE set of spacing/type rules
 * every MFA modal + stepper step shares, so the four modals and the gate
 * stepper can't drift apart again.
 *
 * Rhythm: 24px side gutters (canonical `spacePx[6]`), 20px from the header
 * (bars or titled row) to the first body element, 6px title → subtitle,
 * 20px subtitle → content, 20px body → footer CTA. Every step title is
 * LEFT-aligned.
 */
export const MFA_GUTTER = f.spacePx[6];

/** Step title (h-level copy inside the body). */
export const mfaStepTitleStyle = {
  margin: '0 0 6px',
  fontSize: f.fontSize.xl,
  fontWeight: f.fontWeight.bold,
  color: fc.neutral.text,
  fontFamily: f.fontFamily.sans,
  letterSpacing: '-0.01em',
  lineHeight: 1.25,
  textAlign: 'left',
};

/** Step subtitle — sits 6px under the title, 20px above the content. */
export const mfaStepSubStyle = {
  margin: `0 0 ${f.spacePx[5]}`,
  fontSize: f.fontSize.sm,
  color: fc.neutral.textSecondary,
  fontFamily: f.fontFamily.sans,
  lineHeight: 1.5,
  textAlign: 'left',
};

/** Wrapper around the stepper/titled header — owns the 20px gap to the body. */
export const mfaHeaderWrapStyle = { flexShrink: 0, marginBottom: f.spacePx[5] };

/**
 * Dialog panel for every MFA `AnimatedModalShell`: 24px sides + top; the
 * bottom is 20px on desktop and 24px (or the home-indicator inset, whichever
 * is larger) on phone/tablet. The card is centered by `modalScrimCentered`,
 * not a bottom sheet, so the inset is a floor rather than additive.
 *
 * `sideways` (a phone held sideways, KAN-1012 / RD-UI-179 item 4): the band between the app header
 * and the bottom menu is only ~245px tall, so the top is 16px and the bottom `max(12px, inset)`.
 * Sides, `maxWidth` and the height cap are unchanged. Absent / false = exactly the layout above.
 */
export function mfaModalPanelStyle(compact, maxWidth = 440, { sideways = false } = {}) {
  let bottom = compact ? `max(${MFA_GUTTER}, env(safe-area-inset-bottom, 0px))` : f.spacePx[5];
  let top = MFA_GUTTER;
  if (sideways) {
    top = f.spacePx[4];
    bottom = `max(${f.spacePx[3]}, env(safe-area-inset-bottom, 0px))`;
  }
  return {
    ...modalFlexColumnShell,
    maxWidth,
    padding: `${top} ${MFA_GUTTER} ${bottom}`,
  };
}

/** Pinned footer row — 20px above the CTA (the modal default is 16px). */
export const mfaFooterRowStyle = { ...modalPinnedFooterRow, marginTop: f.spacePx[5] };

/*
 * A phone held sideways (KAN-1012 / RD-UI-179 item 4) - the SAME rhythm, tightened so the title,
 * subtitle, six code boxes and the main button all fit the ~245px band without scrolling:
 * header -> body 8px (was 20), title lg with no gap under it (was xl + 6), subtitle -> content 8px
 * (was 20), footer row -> button 8px (was 16 / 20); the step header itself goes compact (bars in
 * the ✕ row, MfaStepperHeader `compact`). Callers pick a variant through the helpers
 * below with `useIsLandscapePhone()`; every one returns today's style when `sideways` is false.
 */
export const MFA_SIDEWAYS_CELL_PX = 44;
export const mfaHeaderWrapSidewaysStyle = { ...mfaHeaderWrapStyle, marginBottom: f.spacePx[2] };
export const mfaStepTitleSidewaysStyle = { ...mfaStepTitleStyle, fontSize: f.fontSize.lg, margin: 0 };
export const mfaStepSubSidewaysStyle = { ...mfaStepSubStyle, margin: `0 0 ${f.spacePx[2]}` };

export const mfaHeaderWrap = (sideways) => (sideways ? mfaHeaderWrapSidewaysStyle : mfaHeaderWrapStyle);
export const mfaStepTitle = (sideways) => (sideways ? mfaStepTitleSidewaysStyle : mfaStepTitleStyle);
export const mfaStepSub = (sideways) => (sideways ? mfaStepSubSidewaysStyle : mfaStepSubStyle);

/**
 * A footer row (`useOverlayFooter().row` or `mfaFooterRowStyle`) with the sideways 8px gap above
 * the main button; the row itself is untouched otherwise (same buttons, same one footer).
 */
export function mfaFooterRowFor(row, sideways) {
  return sideways ? { ...row, marginTop: f.spacePx[2] } : row;
}

/**
 * Disabled filled CTA — keeps the brand (or danger) fill at reduced opacity
 * with white text, the stepper's "Verificar" treatment. Never the grey
 * `overlayActionDisabled` box (grey-on-grey read as broken on device).
 */
export function mfaDisabledFill(base, disabled) {
  if (!disabled) return base;
  return { ...base, opacity: 0.5, cursor: 'not-allowed', boxShadow: 'none' };
}

/** Full-width primary CTA used by every MFA footer (44px touch at compact). */
export function mfaPrimaryCta(disabled, compact = false) {
  return mfaDisabledFill({
    ...p.buttonPrimary,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: compact ? 44 : 40,
    boxSizing: 'border-box',
  }, disabled);
}

/** Full-width secondary CTA (e.g. "Start over", "Retry"). */
export function mfaSecondaryCta(disabled, compact = false) {
  return {
    ...p.buttonSecondary,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: compact ? 44 : 40,
    boxSizing: 'border-box',
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

/** Centered success/neutral "moment" column (done screens). */
export const mfaDoneColumnStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' };
export const mfaDoneTitleStyle = { ...mfaStepTitleStyle, textAlign: 'center', margin: `${f.spacePx[4]} 0 0` };
export const mfaDoneSubStyle = { ...mfaStepSubStyle, textAlign: 'center', margin: '6px 0 0' };

/**
 * Clock a collapsing MFA header rides so it moves in lockstep with the body's
 * `AutoHeight`. AutoHeight picks its curve from the coarse-pointer media query
 * (not from viewport width), so this mirrors that decision; `compact`
 * (phone/tablet) is only the fallback where `matchMedia` is unavailable.
 */
export function mfaMorphTiming(compact) {
  const coarse = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(hover: none) and (pointer: coarse)').matches
    : Boolean(compact);
  return coarse
    ? { durationMs: AUTO_HEIGHT_MOBILE_MS, easing: AUTO_HEIGHT_MOBILE_EASE }
    : { durationMs: AUTO_HEIGHT_WEB_MS, easing: AUTO_HEIGHT_WEB_EASE };
}
